/**
 * Stone Investment Theme Currency Switcher
 * 
 * Minimal, elegant currency selector matching the Concept One aesthetic.
 * Integrates with CurrencyContext for app-wide currency conversion.
 */

'use client';

import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import type { Currency } from '@/lib/config/app.config';
import { CURRENCY_CONFIG, DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '@/lib/config/app.config';
import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';

interface CurrencySwitcherProps {
  className?: string;
  messages?: Record<string, unknown>;
}

/**
 * StoneInvestmentCurrencySwitcher
 * 
 * Minimal currency selector that matches the Stone Investment aesthetic:
 * - Uppercase tracking-widest text
 * - Subtle hover states
 * - Clean dropdown with serif typography
 */
export function StoneInvestmentCurrencySwitcher({ className = '', messages }: CurrencySwitcherProps) {
  const accessMessages = ((messages?.stone as Record<string, unknown>)?.accessibility || {}) as Record<string, string>;
  const currencyContext = useCurrencyOptional();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCurrency = currencyContext?.selectedCurrency ?? DEFAULT_CURRENCY;
  const isLoading = currencyContext?.isLoading ?? false;

  // Handle currency selection
  const handleSelect = useCallback((currency: Currency) => {
    if (currencyContext) {
      currencyContext.setSelectedCurrency(currency);
    }
    setIsOpen(false);
  }, [currencyContext]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Current currency config available for potential future display enhancements
  void CURRENCY_CONFIG[selectedCurrency as Currency];

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5
          text-xs uppercase tracking-[0.25em] font-sans font-medium
          transition-opacity duration-300
          hover:opacity-70
          ${isLoading ? 'opacity-50' : ''}
        `}
        aria-label={`${selectedCurrency} – ${accessMessages.selectCurrency || 'Select currency'}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{selectedCurrency}</span>
        {isLoading ? (
          <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <ChevronDown 
            size={14} 
            strokeWidth={1.5}
            className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`
            absolute top-full right-0 mt-4 z-50
            min-w-[180px] max-w-[calc(100vw-2rem)]
            bg-stone-50 border border-stone-200
            shadow-lg
            transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
          `}
          role="listbox"
          aria-label={accessMessages.currencyOptions || 'Currency options'}
        >
          {SUPPORTED_CURRENCIES.map((currency) => {
            const config = CURRENCY_CONFIG[currency];
            const isSelected = currency === selectedCurrency;

            return (
              <button
                key={currency}
                type="button"
                onClick={() => handleSelect(currency)}
                className={`
                  w-full flex items-center justify-between
                  px-5 py-3
                  text-left
                  transition-colors duration-200
                  ${isSelected 
                    ? 'bg-stone-100 text-stone-900' 
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                  }
                `}
                role="option"
                aria-selected={isSelected}
              >
                <span className="flex items-center gap-3">
                  <span className="font-serif text-lg">{config?.symbol ?? currency}</span>
                  <span className="font-sans text-xs uppercase tracking-[0.15em]">{currency}</span>
                </span>
                {isSelected && (
                  <span className="text-stone-900">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StoneInvestmentCurrencySwitcher;
